import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import queryString, { ParsedQuery } from 'query-string';
import OvSurveyBuilder from '../UI/organisms/OvSurveyBuilder';
import { SurveyType } from '../../common/model/type/survey-type.enum';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import PollIcon from '@mui/icons-material/Poll';
import { Survey } from '../../common/model/dto/survey/survey';
import OvBackButton from '../UI/atoms/OvBackButton';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminNpsSurveyService } from '../../services/admin/admin-nps-survey.service';
import { QuestionTypeEnum } from '../../common/model/dto/survey/question-type.enum';
import { CURRENT_NPS_SURVEY_QUERY_KEY } from '../../hooks/use-admin-current-nps-survey';
import { getAxiosErrorMessage } from '../../common/utils/helpers';

function hasSingleNpsScoreQuestion({ questions }: Survey): boolean {
  const scoreQuestions =
    questions?.filter(({ represent_nps_score }) => !!represent_nps_score) ?? [];

  return (
    scoreQuestions.length === 1 &&
    scoreQuestions[0].type === QuestionTypeEnum.NPS
  );
}

const NpsSurveyBuilder: FC = () => {
  const { t } = useTranslation();
  const { id }: ParsedQuery = queryString.parse(window.location.search);
  const navigate: NavigateFunction = useNavigate();
  const queryClient = useQueryClient();
  const [survey, setSurvey] = useState<Survey>();

  const { data: savedSurvey, error: surveyError } = useQuery({
    queryKey: ['admin-nps-survey', id],
    queryFn: () => AdminNpsSurveyService.getNpsSurveyById(id as string),
    enabled: !!id,
  });

  useEffect(() => {
    if (savedSurvey) {
      setSurvey(savedSurvey);
    }
  }, [savedSurvey]);

  const saveSurvey = useMutation({
    mutationFn: async (npsSurvey: Survey) => {
      if (!id) {
        return AdminNpsSurveyService.createNpsSurvey(npsSurvey);
      }

      if (!hasSingleNpsScoreQuestion(npsSurvey)) {
        throw new Error(
          'Invalid survey configuration: One NPS question must represent the score.'
        );
      }

      return AdminNpsSurveyService.updateNpsSurvey(npsSurvey);
    },
    onSuccess: (npsSurvey) => {
      queryClient.setQueryData(CURRENT_NPS_SURVEY_QUERY_KEY, npsSurvey);
      queryClient.setQueryData(['admin-nps-survey', npsSurvey.id], npsSurvey);
      navigate(`/surveys/nps/setup`);
    },
  });

  const error = surveyError ?? saveSurvey.error;

  const handleNpsSurveySave = (npsSurvey: Survey) => {
    saveSurvey.mutate(id ? { ...npsSurvey, id: id as string } : npsSurvey);
  };

  const handleSurveyChange = (values: Partial<Survey>) => {
    setSurvey({ ...survey, ...values });
  };

  return (
    <Container>
      <Header>
        <OvBackButton label={t('common.actions.back')} />
        <OvPageTitle
          title={t(id ? 'survey.updateSurvey' : 'survey.create')}
          icon={<StyledPollIcon />}
        />
      </Header>

      <BuilderWrapper>
        <OvSurveyBuilder
          survey={survey}
          surveyType={SurveyType.NPS}
          onChange={handleSurveyChange}
          onSave={handleNpsSurveySave}
        />
      </BuilderWrapper>

      {error && (
        <ErrorMessageContainer>
          <OvErrorMessage message={getAxiosErrorMessage(error)} />
        </ErrorMessageContainer>
      )}
    </Container>
  );
};

export default NpsSurveyBuilder;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StyledPollIcon = styled(PollIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const BuilderWrapper = styled.div`
  margin-top: 1.5rem;
`;

const ErrorMessageContainer = styled.div`
  margin-top: 1rem;
`;
